import { useState, useEffect, useRef } from "react";
import { RecruiterShell } from "@/shell/RecruiterShell";
import { api, ApiError } from "@/lib/api";
import type { RecruiterSettingsData } from "@/lib/types";

// ── Design tokens ─────────────────────────────────────────────────────────────
const T = {
  ink: "#17131F",
  paper: "#FBFAFE",
  accent: "var(--accent)",
  accentLight: "var(--accent-50)",
  slate: "#7C7590",
  white: "#FFFFFF",
  border: "#E8E4DC",
  danger: "#B91C1C",
  dangerLight: "rgba(185,28,28,0.08)",
  dangerBorder: "rgba(185,28,28,0.35)",
  good: "#1A7A4A",
} as const;

const cardStyle: React.CSSProperties = {
  background: T.white,
  borderRadius: 12,
  padding: 24,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
};

const monoLabel: React.CSSProperties = {
  fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  color: T.accent,
};

const fieldLabel: React.CSSProperties = {
  display: "block",
  fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.10em",
  textTransform: "uppercase" as const,
  color: T.slate,
  marginBottom: 6,
};

const inputStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "9px 12px",
  borderRadius: 8,
  border: `1px solid ${T.border}`,
  fontSize: 13.5,
  color: T.ink,
  background: T.white,
  outline: "none",
  boxSizing: "border-box" as const,
  fontFamily: "inherit",
};

const inputDisabledStyle: React.CSSProperties = {
  ...inputStyle,
  background: "#F3F0EA",
  color: T.slate,
  cursor: "not-allowed",
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  appearance: "none" as const,
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%236B7A8C' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center",
  paddingRight: 36,
  cursor: "pointer",
};

const tealBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: T.accent,
  color: T.white,
  border: "none",
  borderRadius: 999,
  padding: "9px 20px",
  fontSize: 13.5,
  fontWeight: 600,
  cursor: "pointer",
  transition: "opacity 0.12s",
  fontFamily: "inherit",
};

const redBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: T.danger,
  color: T.white,
  border: "none",
  borderRadius: 999,
  padding: "9px 20px",
  fontSize: 13.5,
  fontWeight: 600,
  cursor: "pointer",
  transition: "opacity 0.12s",
  fontFamily: "inherit",
};

// ── Toast ─────────────────────────────────────────────────────────────────────
interface ToastState {
  message: string;
  type: "success" | "error";
}

function Toast({ toast, onDone }: { toast: ToastState | null; onDone: () => void }) {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!toast) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onDone();
    }, 3000);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [toast, onDone]);

  if (!toast) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 16,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1000,
        background: toast.type === "success" ? T.good : T.danger,
        color: T.white,
        borderRadius: 8,
        padding: "10px 20px",
        fontSize: 13.5,
        fontWeight: 600,
        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        whiteSpace: "nowrap",
        pointerEvents: "none",
      }}
    >
      {toast.message}
    </div>
  );
}

// ── Field group helper ────────────────────────────────────────────────────────
function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      <label style={fieldLabel}>{label}</label>
      {children}
    </div>
  );
}

// ── Branding card ─────────────────────────────────────────────────────────────
function BrandingCard() {
  return (
    <div style={cardStyle}>
      <p style={{ ...monoLabel, marginBottom: 4 }}>Branding</p>
      <h2 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 700, color: T.ink }}>
        Your agency brand
      </h2>
      <p style={{ margin: "0 0 20px", fontSize: 13.5, color: T.slate, lineHeight: 1.5 }}>
        Manage your logo, display style, colours and how candidates experience your branded portal.
      </p>
      <a
        href="/agency/brand-setup"
        style={{
          ...tealBtn,
          textDecoration: "none",
          display: "inline-flex",
        }}
      >
        Open branding →
      </a>
    </div>
  );
}

// ── Invite defaults card ──────────────────────────────────────────────────────
interface InviteDefaultsCardProps {
  defaultPersona: string;
  defaultMode: string;
  defaultExpiry: number;
  personas: Array<{ id: string; label: string }>;
  modes: Array<{ id: string; label: string }>;
  onToast: (msg: string, type: "success" | "error") => void;
}

function InviteDefaultsCard({
  defaultPersona,
  defaultMode,
  defaultExpiry,
  personas,
  modes,
  onToast,
}: InviteDefaultsCardProps) {
  const [persona, setPersona] = useState(defaultPersona);
  const [mode, setMode] = useState(defaultMode);
  const [expiry, setExpiry] = useState(String(defaultExpiry));
  const [saving, setSaving] = useState(false);

  const EXPIRY_OPTIONS = [
    { value: "3", label: "3 days" },
    { value: "7", label: "7 days" },
    { value: "14", label: "14 days" },
    { value: "30", label: "30 days" },
  ];

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.patch("/recruiter/settings/defaults", {
        default_persona: persona,
        default_mode: mode,
        default_expiry: Number(expiry),
      });
      onToast("Invite defaults saved.", "success");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to save defaults.";
      onToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <p style={{ ...monoLabel, marginBottom: 4 }}>Invite defaults</p>
      <h2 style={{ margin: "0 0 20px", fontSize: 17, fontWeight: 700, color: T.ink }}>
        Default invite settings
      </h2>

      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 20 }}>
        <Field label="Default persona">
          <select
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            style={selectStyle}
          >
            {personas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Default mode">
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            style={selectStyle}
          >
            {modes.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Default expiry">
          <select
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            style={selectStyle}
          >
            {EXPIRY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        style={{ ...tealBtn, opacity: saving ? 0.6 : 1 }}
      >
        {saving ? "Saving…" : "Save defaults"}
      </button>
    </div>
  );
}

// ── Agency details card ───────────────────────────────────────────────────────
interface AgencyDetailsCardProps {
  agencyName: string;
  contactName: string;
  email: string;
  onToast: (msg: string, type: "success" | "error") => void;
}

function AgencyDetailsCard({
  agencyName,
  contactName,
  email,
  onToast,
}: AgencyDetailsCardProps) {
  const [name, setName] = useState(agencyName);
  const [contact, setContact] = useState(contactName);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.patch("/recruiter/settings/account", {
        agency_name: name,
        contact_name: contact,
      });
      onToast("Agency details saved.", "success");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to save details.";
      onToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <p style={{ ...monoLabel, marginBottom: 4 }}>Agency details</p>
      <h2 style={{ margin: "0 0 20px", fontSize: 17, fontWeight: 700, color: T.ink }}>
        Account information
      </h2>

      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 20 }}>
        <Field label="Agency name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
          />
        </Field>

        <Field label="Contact name">
          <input
            type="text"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            style={inputStyle}
          />
        </Field>

        <Field label="Email">
          <input
            type="email"
            value={email}
            disabled
            readOnly
            style={inputDisabledStyle}
          />
          <span
            style={{
              marginTop: 4,
              fontSize: 11.5,
              color: T.slate,
              fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
            }}
          >
            Email cannot be changed. Contact support if needed.
          </span>
        </Field>
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        style={{ ...tealBtn, opacity: saving ? 0.6 : 1 }}
      >
        {saving ? "Saving…" : "Save details"}
      </button>
    </div>
  );
}

// ── Change password card ──────────────────────────────────────────────────────
function ChangePasswordCard({
  onToast,
}: {
  onToast: (msg: string, type: "success" | "error") => void;
}) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const handleUpdate = async () => {
    if (saving) return;
    if (next !== confirm) {
      onToast("New passwords do not match.", "error");
      return;
    }
    if (next.length < 8) {
      onToast("New password must be at least 8 characters.", "error");
      return;
    }
    setSaving(true);
    try {
      const result = await api.post<{ ok: boolean; error?: string }>("/recruiter/settings/password", {
        current_password: current,
        new_password: next,
        confirm_password: confirm,
      });
      if (!result.ok) {
        onToast(result.error || "Failed to update password.", "error");
        return;
      }
      onToast("Password updated.", "success");
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to update password.";
      onToast(msg, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={cardStyle}>
      <p style={{ ...monoLabel, marginBottom: 4 }}>Security</p>
      <h2 style={{ margin: "0 0 20px", fontSize: 17, fontWeight: 700, color: T.ink }}>
        Change password
      </h2>

      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginBottom: 20 }}>
        <Field label="Current password">
          <input
            type="password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            style={inputStyle}
          />
        </Field>

        <Field label="New password">
          <input
            type="password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            autoComplete="new-password"
            style={inputStyle}
          />
        </Field>

        <Field label="Confirm new password">
          <input
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            style={inputStyle}
          />
        </Field>
      </div>

      <button
        onClick={handleUpdate}
        disabled={saving || !current || !next || !confirm}
        style={{
          ...tealBtn,
          opacity: saving || !current || !next || !confirm ? 0.5 : 1,
          cursor: saving || !current || !next || !confirm ? "not-allowed" : "pointer",
        }}
      >
        {saving ? "Updating…" : "Update password"}
      </button>
    </div>
  );
}

// ── Delete account card ───────────────────────────────────────────────────────
function DeleteAccountCard({
  agencyName,
  onToast,
}: {
  agencyName: string;
  onToast: (msg: string, type: "success" | "error") => void;
}) {
  const [confirmName, setConfirmName] = useState("");
  const [deleting, setDeleting] = useState(false);
  const nameMatches = confirmName.trim().toLowerCase() === agencyName.trim().toLowerCase();

  const handleDelete = async () => {
    if (deleting || !nameMatches) return;
    setDeleting(true);
    try {
      const delResult = await api.delete<{ ok: boolean; error?: string }>("/recruiter/settings", { confirm_name: confirmName });
      if (!delResult.ok) {
        onToast(delResult.error || "Failed to delete account.", "error");
        setDeleting(false);
        return;
      }
      window.location.href = "/auth/login";
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Failed to delete account.";
      onToast(msg, "error");
      setDeleting(false);
    }
  };

  return (
    <div
      style={{
        ...cardStyle,
        border: `1.5px solid ${T.dangerBorder}`,
        background: T.white,
      }}
    >
      <h2
        style={{
          margin: "0 0 10px",
          fontSize: 17,
          fontWeight: 700,
          color: T.danger,
        }}
      >
        Delete account
      </h2>
      <p
        style={{
          margin: "0 0 20px",
          fontSize: 13.5,
          color: T.slate,
          lineHeight: 1.55,
        }}
      >
        Permanently deletes your agency account, all candidates, invites, and session data. This
        action cannot be undone. Your candidates will lose access immediately.
      </p>

      <p
        style={{
          margin: "0 0 8px",
          fontFamily: "var(--font-mono, 'Geist Mono', monospace)",
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: "0.08em",
          color: T.slate,
          textTransform: "uppercase" as const,
        }}
      >
        Type your agency name to confirm:{" "}
        <span style={{ color: T.ink }}>{agencyName.toUpperCase()}</span>
      </p>

      <input
        type="text"
        value={confirmName}
        onChange={(e) => setConfirmName(e.target.value)}
        placeholder={agencyName}
        style={{
          ...inputStyle,
          marginBottom: 16,
          borderColor: confirmName && !nameMatches ? T.danger : T.border,
        }}
      />

      <button
        onClick={handleDelete}
        disabled={deleting || !nameMatches}
        style={{
          ...redBtn,
          opacity: deleting || !nameMatches ? 0.45 : 1,
          cursor: deleting || !nameMatches ? "not-allowed" : "pointer",
        }}
      >
        {deleting ? "Deleting…" : "Delete account permanently"}
      </button>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function RecruiterSettings() {
  const [data, setData] = useState<RecruiterSettingsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = (message: string, type: "success" | "error") => {
    setToast({ message, type });
  };

  const clearToast = () => setToast(null);

  useEffect(() => {
    api
      .get<RecruiterSettingsData>("/recruiter/settings")
      .then(setData)
      .catch((err: Error) => setError(err.message ?? "Failed to load settings"));
  }, []);

  if (error) {
    return (
      <RecruiterShell activeNav="settings">
        <div style={{ padding: 32, color: T.danger, fontSize: 14 }}>Error: {error}</div>
      </RecruiterShell>
    );
  }

  if (!data) {
    return (
      <RecruiterShell activeNav="settings">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
            fontSize: 14,
            color: T.slate,
          }}
        >
          Loading…
        </div>
      </RecruiterShell>
    );
  }

  const { agency, personas, modes } = data;

  return (
    <RecruiterShell activeNav="settings">
      <Toast toast={toast} onDone={clearToast} />

      {/* ── Page header ────────────────────────────────────────────────── */}
      <div style={{ marginBottom: 28 }}>
        <h1
          style={{
            margin: 0,
            fontSize: 26,
            fontWeight: 700,
            color: T.ink,
            lineHeight: 1.2,
          }}
        >
          Settings
        </h1>
        <p style={{ margin: "4px 0 0", fontSize: 13.5, color: T.slate }}>
          Manage your agency account, branding, and invite preferences.
        </p>
      </div>

      {/* ── Two-column layout ───────────────────────────────────────────── */}
      <div
        className="settings-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 20,
          alignItems: "start",
        }}
      >
        {/* ── LEFT COLUMN ─────────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <BrandingCard />

          <InviteDefaultsCard
            defaultPersona={agency.default_persona}
            defaultMode={agency.default_mode}
            defaultExpiry={agency.default_expiry}
            personas={personas}
            modes={modes}
            onToast={showToast}
          />
        </div>

        {/* ── RIGHT COLUMN ────────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <AgencyDetailsCard
            agencyName={agency.agency_name}
            contactName={agency.contact_name}
            email={agency.email}
            onToast={showToast}
          />

          <ChangePasswordCard onToast={showToast} />

          <DeleteAccountCard agencyName={agency.agency_name} onToast={showToast} />
        </div>
      </div>

      {/* Responsive collapse to single column on mobile */}
      <style>{`
        @media (max-width: 720px) {
          .settings-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </RecruiterShell>
  );
}
